/* Elwardany product cards — Image Presentation System (B4 production; from the approved B3 prototype).
   Catalogue photos are square canvases, but products fill them very differently (a hose roll edge to edge,
   a power supply as a small island). For each card photo this script measures where the product is (tiny
   same-origin canvas, background read from the corners) and sets a bounded, adaptive scale + centring so
   products reach a consistent perceived size. Never crops, never distorts, never alters the photo.
   Scene photos and any image it cannot read keep the neutral default. Results are cached per session.
   The second (hover) photo is fetched only on the first pointer hover — touch devices never download it. */
(() => {
  const SELECTOR = '.elw-card .elw-card__img img';
  const SIZE = 64;
  const CACHE = 'elw-card-fit1:';
  document.documentElement.classList.add('elw-fit-on');

  const key = (img) => CACHE + (img.currentSrc || img.src).split('?')[0].replace(/_\d+x\d*(?=\.)/, '');

  function measure(img) {
    const c = document.createElement('canvas');
    c.width = c.height = SIZE;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, SIZE, SIZE);
    const d = ctx.getImageData(0, 0, SIZE, SIZE).data;
    const px = (x, y) => { const i = (y * SIZE + x) * 4; return [d[i], d[i + 1], d[i + 2], d[i + 3]]; };
    const corners = [px(1, 1), px(SIZE - 2, 1), px(1, SIZE - 2), px(SIZE - 2, SIZE - 2)];
    const bg = corners[0];
    const dist = (a, b) => Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[2] - b[2]), Math.abs(a[3] - b[3]));
    // Corners disagree → a scene (lifestyle photo, gradient): shown whole, edge to edge.
    if (corners.some((k) => dist(k, bg) > 24)) return { fit: 1, tx: 0, ty: 0, scene: 1 };
    let x0 = SIZE, y0 = SIZE, x1 = -1, y1 = -1;
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        const p = px(x, y);
        if (bg[3] < 40 ? p[3] > 40 : dist(p, bg) > 22) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
    }
    if (x1 < 0) return null;
    const bw = (x1 - x0 + 1) / SIZE, bh = (y1 - y0 + 1) / SIZE;
    const cx = (x0 + x1 + 1) / 2 / SIZE, cy = (y0 + y1 + 1) / 2 / SIZE;
    // Compact products ≈74% of the frame; long/thin ones may use more of their long side. Never above 88%;
    // scale bounded 0.8–1.6 (conservative: low-resolution photos are never blown up beyond that).
    const long = Math.max(bw, bh), short = Math.min(bw, bh);
    const target = Math.min(0.88, 0.74 + 0.14 * (1 - short / long));
    const fit = Math.max(0.8, Math.min(1.6, target / long));
    const r = (v) => Math.round(v * 1000) / 1000;
    const tinted = bg[3] > 200 && Math.min(bg[0], bg[1], bg[2]) < 246;
    return { fit: r(fit), tx: r((0.5 - cx) * fit), ty: r((0.5 - cy) * fit), bg: tinted ? `rgb(${bg[0]} ${bg[1]} ${bg[2]})` : '' };
  }

  function apply(img, m) {
    const s = img.style;
    if (m) {
      // Low-resolution guard: never scale a photo beyond what its rendered pixels can carry.
      let fit = m.fit;
      const need = img.getBoundingClientRect().width * (window.devicePixelRatio || 1) * fit;
      if (img.naturalWidth && need > img.naturalWidth * 1.15) fit = Math.max(0.86, Math.min(fit, (img.naturalWidth * 1.15) / (need / fit)));
      s.setProperty('--elw-fit', fit);
      s.setProperty('--elw-tx', (m.tx / m.fit * fit * 100) + '%');
      s.setProperty('--elw-ty', (m.ty / m.fit * fit * 100) + '%');
      img.dataset.elwBg = m.scene ? 'scene' : (m.bg || 'stage');
    } else {
      img.dataset.elwBg = 'stage';
    }
    img.classList.add('is-fitted');
    sync(img.closest('.elw-card__img'));
  }

  // The card stage follows the main photo; the hover photo is used only when its backdrop matches.
  function sync(wrap) {
    if (!wrap) return;
    const main = wrap.querySelector('.elw-card__img-main.is-fitted');
    const alt = wrap.querySelector('.elw-card__img-alt.is-fitted');
    const media = wrap.closest('.elw-card__media');
    if (main && media) {
      const bg = main.dataset.elwBg;
      media.style.background = bg === 'stage' ? '' : (bg === 'scene' ? '#fff' : bg);
      media.classList.toggle('is-tinted', bg !== 'stage');
    }
    if (main && alt) alt.classList.toggle('is-compatible', alt.dataset.elwBg === main.dataset.elwBg && alt.dataset.elwBg !== 'scene');
  }

  function process(img) {
    if (img.dataset.elwFit) return;
    img.dataset.elwFit = '1';
    let m;
    try { const c = sessionStorage.getItem(key(img)); if (c !== null) m = JSON.parse(c); } catch (e) { /* storage unavailable */ }
    if (m === undefined) {
      try { m = measure(img); } catch (e) { m = null; }
      try { sessionStorage.setItem(key(img), JSON.stringify(m)); } catch (e) { /* ignore */ }
    }
    apply(img, m);
  }

  function watch(img) {
    if (img.classList.contains('elw-card__img-alt') && !img.getAttribute('src')) return; // not requested yet
    if (img.complete && img.naturalWidth) process(img);
    else {
      img.addEventListener('load', () => process(img), { once: true });
      img.addEventListener('error', () => apply(img, null), { once: true });
    }
  }

  // Hover photo: load on the first pointer hover (desktop / fine pointer only).
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  document.addEventListener('pointerover', (e) => {
    if (!fine.matches || e.pointerType !== 'mouse') return;
    const card = e.target.closest && e.target.closest('.elw-card');
    if (!card || card.classList.contains('is-soldout')) return;
    const alt = card.querySelector('.elw-card__img-alt[data-srcset]');
    if (!alt) return;
    alt.sizes = alt.dataset.sizes || '';
    alt.srcset = alt.dataset.srcset; alt.src = alt.dataset.src;
    alt.removeAttribute('data-srcset');
    watch(alt);
  }, { passive: true });

  const init = (root = document) => root.querySelectorAll(SELECTOR).forEach(watch);
  init();
  new MutationObserver((list) => list.forEach((r) => r.addedNodes.forEach((n) => n.nodeType === 1 && init(n)))).observe(document.body, { childList: true, subtree: true });
  // Safety: never leave a main photo hidden (slow decode, unusual markup).
  const reveal = () => document.querySelectorAll('.elw-card .elw-card__img img.elw-card__img-main:not(.is-fitted)').forEach((i) => { if (i.complete) apply(i, null); });
  setTimeout(reveal, 1500);
  addEventListener('load', () => setTimeout(reveal, 1500));
})();
